/** Minimal vector/matrix helpers for `pca.ts` and `kmeans.ts` — enough linear
 *  algebra for a handful of low-dimensional feature vectors (one lookalike's
 *  worth of fields), not a general-purpose numeric library. No dependency
 *  pulled in for it: the whole surface below is a page of code. */

export function dot(a: readonly number[], b: readonly number[]): number {
  let sum = 0;
  for (let i = 0; i < a.length; i += 1) sum += a[i]! * b[i]!;
  return sum;
}

export function subtract(a: readonly number[], b: readonly number[]): number[] {
  return a.map((value, i) => value - b[i]!);
}

export function scale(a: readonly number[], factor: number): number[] {
  return a.map((value) => value * factor);
}

export function norm(a: readonly number[]): number {
  return Math.sqrt(dot(a, a));
}

export function normalize(a: readonly number[]): number[] {
  const length = norm(a);
  return length > 1e-12 ? scale(a, 1 / length) : a.slice();
}

export function meanVector(vectors: readonly (readonly number[])[]): number[] {
  const first = vectors[0];
  if (!first) return [];
  const dims = first.length;
  const sums = new Array<number>(dims).fill(0);
  for (const vector of vectors) {
    for (let d = 0; d < dims; d += 1) sums[d] = sums[d]! + vector[d]!;
  }
  return sums.map((sum) => sum / vectors.length);
}

/** Population standard deviation per dimension — the divisor for
 *  standardisation, not a sample-corrected estimate; this is describing the
 *  population sampled, not inferring about a larger one. */
export function stdVector(
  vectors: readonly (readonly number[])[],
  mean: readonly number[],
): number[] {
  const dims = mean.length;
  const sums = new Array<number>(dims).fill(0);
  for (const vector of vectors) {
    for (let d = 0; d < dims; d += 1)
      sums[d] = sums[d]! + (vector[d]! - mean[d]!) ** 2;
  }
  return sums.map((sum) => Math.sqrt(sum / vectors.length));
}

/** Zero-means and unit-scales every dimension, so no single feature (say, age
 *  spanning 40 units) dominates a Euclidean distance or a covariance next to
 *  one spanning 0–1 (say, tech savviness). A dimension with zero variance —
 *  every lookalike sharing one pool value — divides by 1 instead of 0 rather
 *  than producing `NaN` down the line. */
export function standardize(vectors: readonly (readonly number[])[]): {
  mean: number[];
  standardized: number[][];
  std: number[];
} {
  const mean = meanVector(vectors);
  const std = stdVector(vectors, mean).map((value) =>
    value > 1e-9 ? value : 1,
  );
  const standardized = vectors.map((vector) =>
    vector.map((value, d) => (value - mean[d]!) / std[d]!),
  );
  return { mean, standardized, std };
}

/** The d×d covariance matrix of a set of (already standardised, but this
 *  works on any) vectors. */
export function covariance(
  vectors: readonly (readonly number[])[],
): number[][] {
  const mean = meanVector(vectors);
  const dims = mean.length;
  const matrix = Array.from({ length: dims }, () =>
    new Array<number>(dims).fill(0),
  );
  for (const vector of vectors) {
    const centered = subtract(vector, mean);
    for (let i = 0; i < dims; i += 1) {
      for (let j = 0; j < dims; j += 1) {
        matrix[i]![j] = matrix[i]![j]! + centered[i]! * centered[j]!;
      }
    }
  }
  for (let i = 0; i < dims; i += 1) {
    for (let j = 0; j < dims; j += 1) {
      matrix[i]![j] = matrix[i]![j]! / vectors.length;
    }
  }
  return matrix;
}

export function matVec(
  matrix: readonly (readonly number[])[],
  vector: readonly number[],
): number[] {
  return matrix.map((row) => dot(row, vector));
}

export function outer(a: readonly number[], b: readonly number[]): number[][] {
  return a.map((ai) => b.map((bj) => ai * bj));
}

export function matSubtract(
  a: readonly (readonly number[])[],
  b: readonly (readonly number[])[],
): number[][] {
  return a.map((row, i) => subtract(row, b[i]!));
}
